import Checklist, {
  BusinessRefundMethodInfoSupportingItemInput,
  PersonalRefundMethodInfoSupportingItemInput,
} from 'jobs/Checklist'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import TaxRefundMethodStore from './TaxRefundMethodStore'
import Job from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { whileActive, withCancellation } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { task } from 'mobx-task'
import { BehaviorSubject } from 'rxjs'

export default class TaxRefundMethodViewModel {
  // Tracks whether the view model is active.
  private readonly _active = new BehaviorSubject<boolean>(false)

  constructor(
    public job: Job,
    public infoGatheringStore: InfoGatheringStore,
    public taxRefundMethodStore: TaxRefundMethodStore,
    private getChecklist: () => Checklist | undefined,
  ) {
    makeObservable(this)

    // reacts to changes in the supporting item to re-fetch the profile.
    toStream(() => {
      return {
        refundMethodVersion:
          this.businessRefundMethodSupportingItemInput?.refundMethodVersion,
        refundMethodId:
          this.businessRefundMethodSupportingItemInput?.refundMethodId,
      }
    }, true)
      .pipe(
        whileActive(this._active),
        switchMap(() => withCancellation(this._onRemoteItemUpdated)),
      )
      .subscribe()
  }

  @computed
  get isRelevant() {
    return (
      this.businessRefundMethodSupportingItem?.completed ||
      this.personalRefundMethodSupportingItem?.completed
    )
  }

  /**
   * The business refund method version.
   */
  @computed
  get businessRefundMethod() {
    if (!this.businessRefundMethodSupportingItemInput?.refundMethodVersion) {
      return null
    }

    return this.taxRefundMethodStore.getTaxRefundMethod(
      this.businessRefundMethodSupportingItemInput?.refundMethodId,
      this.businessRefundMethodSupportingItemInput?.refundMethodVersion,
    )
  }

  /**
   * The personal refund method version.
   */
  @computed
  get personalRefundMethod() {
    if (!this.personalRefundMethodSupportingItemInput?.refundMethodVersion) {
      return null
    }

    return this.taxRefundMethodStore.getTaxRefundMethod(
      this.personalRefundMethodSupportingItemInput?.refundMethodId,
      this.personalRefundMethodSupportingItemInput?.refundMethodVersion,
    )
  }

  /**
   * The business refund method delivery method display.
   */
  @computed
  get businessRefundMethodDeliveryMethodDisplay() {
    return this.businessRefundMethod?.deliveryMethod?.typeDisplay
  }

  /**
   * The personal refund method delivery method display.
   */
  @computed
  get personalRefundMethodDeliveryMethodDisplay() {
    return this.personalRefundMethod?.deliveryMethod?.typeDisplay
  }

  /**
   * The checklist.
   */
  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * The business section.
   */
  @computed
  get businessSection() {
    return this.checklist?.businessInfoSection
  }

  /**
   * The personal section.
   */
  @computed
  get personalSection() {
    return this.checklist?.personalInfoSection
  }

  /**
   * The supporting item.
   */
  @computed
  get businessRefundMethodSupportingItem() {
    return this.businessSection?.businessRefundMethodInfoSupportingItem
  }

  /**
   * The supporting item.
   */
  @computed
  get personalRefundMethodSupportingItem() {
    return this.personalSection?.personalRefundMethodInfoSupportingItem
  }

  /**
   * The supporting item input.
   */
  @computed
  get businessRefundMethodSupportingItemInput() {
    return this.businessRefundMethodSupportingItem
      ?.input as BusinessRefundMethodInfoSupportingItemInput
  }

  /**
   * The supporting item input.
   */
  @computed
  get personalRefundMethodSupportingItemInput() {
    return this.personalRefundMethodSupportingItem
      ?.input as PersonalRefundMethodInfoSupportingItemInput
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this._active.next(true)
    this.fetchRefundMethods()
  })

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Updates the business refund method.
   */
  private _onRemoteItemUpdated = task(async (ct: CancellationToken) => {
    if (this.businessRefundMethodSupportingItem?.completed) {
      await ct.race(
        this.taxRefundMethodStore.fetchBusinessTaxRefundMethodForJob(
          this.job.id,
        ),
      )
    }

    if (this.personalRefundMethodSupportingItem?.completed) {
      await ct.race(
        this.taxRefundMethodStore.fetchPersonalTaxRefundMethodForJob(
          this.job.id,
        ),
      )
    }
  })

  private async fetchRefundMethods() {
    if (this.businessRefundMethodSupportingItem?.completed) {
      await this.taxRefundMethodStore.fetchBusinessTaxRefundMethodForJob(
        this.job.id,
      )
    }

    if (this.personalRefundMethodSupportingItem?.completed) {
      await this.taxRefundMethodStore.fetchPersonalTaxRefundMethodForJob(
        this.job.id,
      )
    }
  }
}
